import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_JOB } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

const initialState = {
  profile: null,
  loading: false,
  error: null,
  fetched: false,
  // For managing experiences and skills
  experienceLoading: false,
  skillLoading: false,
  // Onboarding status
  onboarding: null,
  onboardingLoading: false,
  onboardingFetched: false,
  shouldRedirectAfterOnboarding: false,
  profileUpdateSuccess: false,
  shouldCompleteOnboarding: false
};

const candidateSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {
    fetchProfileRequest(state) {
      state.loading = true;
      state.error = null;
    },
    fetchProfileSuccess(state, action) {
      state.loading = false;
      state.profile = action.payload.profile;
      state.fetched = true;
      state.profileUpdateSuccess = true;
    },
    fetchProfileFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    clearProfile(state) {
      state.profile = null;
      state.fetched = false;
      state.error = null;
    },
    // Experience actions
    experienceActionRequest(state) {
      state.experienceLoading = true;
    },
    experienceActionSuccess(state, action) {
      state.experienceLoading = false;
      // Update the profile's experiences array
      if (state.profile && action.payload.experiences) {
        state.profile.experiences = action.payload.experiences;
      }
    },
    experienceActionFailure(state) {
      state.experienceLoading = false;
    },
    // Skill actions
    skillActionRequest(state) {
      state.skillLoading = true;
    },
    skillActionSuccess(state, action) {
      state.skillLoading = false;
      // Update the profile's skills array
      if (state.profile && action.payload.skills) {
        state.profile.skills = action.payload.skills;
      }
    },
    skillActionFailure(state) {
      state.skillLoading = false;
    },
    // Onboarding actions
    fetchOnboardingRequest(state) {
      state.onboardingLoading = true;
    },
    fetchOnboardingSuccess(state, action) {
      state.onboardingLoading = false;
      state.onboarding = action.payload;
      state.onboardingFetched = true;
    },
    fetchOnboardingFailure(state) {
      state.onboardingLoading = false;
      state.onboardingFetched = true;
    },
    updateOnboardingStatus(state, action) {
      state.onboarding = action.payload;
      // Set redirect flag when onboarding is completed
      if (action.payload?.status === 'COMPLETED') {
        state.shouldRedirectAfterOnboarding = true;
      }
    },
    clearOnboardingRedirectFlag(state) {
      state.shouldRedirectAfterOnboarding = false;
    },
    setProfileUpdateSuccess(state, action) {
      state.profileUpdateSuccess = action.payload;
    },
    setShouldCompleteOnboarding(state, action) {
      state.shouldCompleteOnboarding = action.payload;
    }
  }
});

export const {
  fetchProfileRequest,
  fetchProfileSuccess,
  fetchProfileFailure,
  clearProfile,
  experienceActionRequest,
  experienceActionSuccess,
  experienceActionFailure,
  skillActionRequest,
  skillActionSuccess,
  skillActionFailure,
  fetchOnboardingRequest,
  fetchOnboardingSuccess,
  fetchOnboardingFailure,
  updateOnboardingStatus,
  clearOnboardingRedirectFlag,
  setProfileUpdateSuccess,
  setShouldCompleteOnboarding
} = candidateSlice.actions;

// Selectors
export const candidateProfileSelector = (state) => state.candidate.profile;
export const candidateLoadingSelector = (state) => state.candidate.loading;
export const candidateErrorSelector = (state) => state.candidate.error;
export const candidateFetchedSelector = (state) => state.candidate.fetched;
export const experienceLoadingSelector = (state) => state.candidate.experienceLoading;
export const skillLoadingSelector = (state) => state.candidate.skillLoading;
export const onboardingSelector = (state) => state.candidate.onboarding;
export const onboardingLoadingSelector = (state) => state.candidate.onboardingLoading;
export const onboardingFetchedSelector = (state) => state.candidate.onboardingFetched;
export const shouldRedirectAfterOnboardingSelector = (state) => state.candidate.shouldRedirectAfterOnboarding;
export const profileUpdateSuccessSelector = (state) => state.candidate.profileUpdateSuccess;
export const shouldCompleteOnboardingSelector = (state) => state.candidate.shouldCompleteOnboarding;

// Thunk to fetch candidate profile
export const fetchCandidateProfile = () => async (dispatch) => {
  console.log('📋 [Candidate Slice] Fetching candidate profile');
  dispatch(fetchProfileRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/profile`;
    console.log(`📡 [Candidate Slice] Fetching from: ${url}`);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    console.log(`📡 [Candidate Slice] Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch profile: ${errorText}`);
    }

    const data = await response.json();
    console.log(`📦 [Candidate Slice] Profile data:`, data);

    const profile = data.data || null;

    dispatch(
      fetchProfileSuccess({
        profile
      })
    );

    return { success: true, profile };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error:', error);

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to load profile',
        severity: Severity.ERROR
      })
    );
    dispatch(
      fetchProfileFailure({
        error: error.message
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to update candidate profile
export const updateCandidateProfile = (profileData) => async (dispatch) => {
  console.log('🔄 [Candidate Slice] Updating candidate profile', profileData);

  dispatch(fetchProfileRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/profile`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify(profileData)
    });

    console.log('📡 [Candidate Slice] Update response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('⚠️ [Candidate Slice] Profile update failed:', response.status, errorText);

      // Handle gracefully - store the submitted data locally in Redux
      // This way the form doesn't lose the user's input
      console.log('📦 [Candidate Slice] Storing submitted profile data locally:', profileData);
      dispatch(
        fetchProfileSuccess({
          profile: profileData
        })
      );

      // Don't show error message, just log it
      console.warn('⚠️ [Candidate Slice] Profile not saved to server, but stored locally');

      return { success: false, profile: profileData, warning: 'stored_locally' };
    }

    const data = await response.json();
    const profile = data.data || profileData; // Fallback to submitted data if null

    console.log('✅ [Candidate Slice] Profile updated successfully');
    console.log('📦 [Candidate Slice] Updated profile data:', JSON.stringify(profile, null, 2));

    dispatch(
      fetchProfileSuccess({
        profile
      })
    );

    dispatch(
      messageUpdated({
        text: 'Profile updated successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true, profile };
  } catch (error) {
    console.warn('⚠️ [Candidate Slice] Network error updating profile:', error);

    // Handle gracefully - store the submitted data locally in Redux
    console.log('📦 [Candidate Slice] Storing submitted profile data locally due to error');
    dispatch(
      fetchProfileSuccess({
        profile: profileData
      })
    );

    // Don't show error message to user, just log it
    console.warn('⚠️ [Candidate Slice] Profile stored locally, will retry on next action');

    return { success: false, profile: profileData, warning: 'network_error' };
  }
};

// ========== EXPERIENCE THUNKS ==========

// Thunk to add experience
export const addExperience = (experienceData) => async (dispatch) => {
  console.log('➕ [Candidate Slice] Adding experience');
  dispatch(experienceActionRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/experience`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify(experienceData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add experience: ${errorText}`);
    }

    const data = await response.json();
    const newExperience = data.data;

    // Mark experience action as complete
    dispatch(experienceActionSuccess({ experiences: [] }));

    // Re-fetch the profile to get updated experiences list
    dispatch(fetchCandidateProfile());

    dispatch(
      messageUpdated({
        text: 'Experience added successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true, experience: newExperience };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error adding experience:', error);
    dispatch(experienceActionFailure());

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to add experience',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to update experience
export const updateExperience = (experienceId, experienceData) => async (dispatch) => {
  console.log('🔄 [Candidate Slice] Updating experience');
  dispatch(experienceActionRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/experience/${experienceId}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify(experienceData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update experience: ${errorText}`);
    }

    // Mark experience action as complete
    dispatch(experienceActionSuccess({ experiences: [] }));

    // Re-fetch the profile to get updated experiences list
    dispatch(fetchCandidateProfile());

    dispatch(
      messageUpdated({
        text: 'Experience updated successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error updating experience:', error);
    dispatch(experienceActionFailure());

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to update experience',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to delete experience
export const deleteExperience = (experienceId) => async (dispatch) => {
  console.log('🗑️ [Candidate Slice] Deleting experience');
  dispatch(experienceActionRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/experience/${experienceId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete experience: ${errorText}`);
    }

    // Mark experience action as complete
    dispatch(experienceActionSuccess({ experiences: [] }));

    // Re-fetch the profile to get updated experiences list
    dispatch(fetchCandidateProfile());

    dispatch(
      messageUpdated({
        text: 'Experience deleted successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error deleting experience:', error);
    dispatch(experienceActionFailure());

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to delete experience',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// ========== SKILL THUNKS ==========

// Thunk to add skill
export const addSkill = (skillData) => async (dispatch) => {
  console.log('➕ [Candidate Slice] Adding skill');
  dispatch(skillActionRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/skill`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify(skillData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add skill: ${errorText}`);
    }

    // Mark skill action as complete
    dispatch(skillActionSuccess({ skills: [] }));

    // Re-fetch the profile to get updated skills list
    dispatch(fetchCandidateProfile());

    dispatch(
      messageUpdated({
        text: 'Skill added successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error adding skill:', error);
    dispatch(skillActionFailure());

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to add skill',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to delete skill
export const deleteSkill = (skillId) => async (dispatch) => {
  console.log('🗑️ [Candidate Slice] Deleting skill');
  dispatch(skillActionRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/skill/${skillId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete skill: ${errorText}`);
    }

    // Mark skill action as complete
    dispatch(skillActionSuccess({ skills: [] }));

    // Re-fetch the profile to get updated skills list
    dispatch(fetchCandidateProfile());

    dispatch(
      messageUpdated({
        text: 'Skill deleted successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error deleting skill:', error);
    dispatch(skillActionFailure());

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to delete skill',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// ========== ONBOARDING THUNKS ==========

// Thunk to fetch onboarding status
export const fetchOnboardingStatus = () => async (dispatch) => {
  console.log('📋 [Candidate Slice] Fetching onboarding status');
  dispatch(fetchOnboardingRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/onboarding`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch onboarding status: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(fetchOnboardingSuccess(onboarding));

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error fetching onboarding:', error);
    dispatch(fetchOnboardingFailure());
    return { success: false, error: error.message };
  }
};

// Thunk to update onboarding progress
export const updateOnboardingProgress = (currentStep) => async (dispatch) => {
  console.log('🔄 [Candidate Slice] Updating onboarding progress');

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/onboarding/progress`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify({ currentStep })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update onboarding progress: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(updateOnboardingStatus(onboarding));

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error updating onboarding progress:', error);
    return { success: false, error: error.message };
  }
};

// Thunk to complete onboarding
export const completeOnboarding = () => async (dispatch, getState) => {
  console.log('✅ [Candidate Slice] Completing onboarding');

  // Get current profile from state to verify data
  const currentProfile = getState().candidate.profile;
  console.log('📦 [Candidate Slice] Current profile in Redux before completing:', JSON.stringify(currentProfile, null, 2));

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/onboarding/complete`;

    console.log('📡 [Candidate Slice] POSTing to complete onboarding:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    console.log('📡 [Candidate Slice] Complete onboarding response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Candidate Slice] Complete onboarding failed:', errorText);
      throw new Error(`Failed to complete onboarding: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    console.log('✅ [Candidate Slice] Onboarding completed successfully:', onboarding);

    dispatch(updateOnboardingStatus(onboarding));

    dispatch(
      messageUpdated({
        text: 'Profile completed successfully!',
        severity: Severity.SUCCESS
      })
    );

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error completing onboarding:', error);

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to complete onboarding',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to skip onboarding
export const skipOnboarding = () => async (dispatch) => {
  console.log('⏭️ [Candidate Slice] Skipping onboarding');

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/candidate/onboarding/skip`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to skip onboarding: ${errorText}`);
    }

    const data = await response.json();
    const onboarding = data.data || null;

    dispatch(updateOnboardingStatus(onboarding));

    return { success: true, onboarding };
  } catch (error) {
    console.error('❌ [Candidate Slice] Error skipping onboarding:', error);
    return { success: false, error: error.message };
  }
};

export default candidateSlice.reducer;

